import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';

const inputClass = "w-full px-3 py-2 border border-[#C9C9C9] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all";
const labelClass = "text-[12px] font-semibold text-[#5C5C5C] block mb-1.5";

const COMMON_LOCALES = [
  { code: 'en', label: 'English' }, { code: 'hi', label: 'Hindi' }, { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' }, { code: 'de', label: 'German' }, { code: 'pt', label: 'Portuguese' },
  { code: 'ar', label: 'Arabic' }, { code: 'zh', label: 'Chinese' },
];

const OPTION_TYPES = new Set(['select', 'radio', 'checkboxGroup']);
const PLACEHOLDER_TYPES = new Set(['text', 'textarea', 'email', 'phone', 'number', 'url', 'select']);

/**
 * Phase 25 — multi-language forms. Translations are pure display-layer
 * overlays (schema.settings.i18n.translations[locale][componentId] →
 * {label, placeholder, helpText, options, checkboxLabel}) merged onto the
 * authored component at render time by FormRenderer — the authoring-
 * language schema itself never changes, so field ids/types/validation/
 * conditions/CRM mapping all keep working unmodified regardless of which
 * language a visitor picks.
 */
export default function LanguagePanel({ pages, i18n, onChange, onClose }) {
  const current = i18n || { enabled: false, defaultLocale: 'en', locales: [], translations: {} };
  const [activeLocale, setActiveLocale] = useState(current.locales?.[0]?.code || '');
  const set = (patch) => onChange({ ...current, ...patch });

  const addLocale = (code) => {
    if (!code || current.locales?.some((l) => l.code === code)) return;
    const label = COMMON_LOCALES.find((l) => l.code === code)?.label || code;
    const locales = [...(current.locales || []), { code, label }];
    set({ enabled: true, locales, defaultLocale: current.defaultLocale || code });
    setActiveLocale(code);
  };
  const removeLocale = (code) => {
    const locales = (current.locales || []).filter((l) => l.code !== code);
    const translations = { ...current.translations };
    delete translations[code];
    set({ locales, translations, defaultLocale: current.defaultLocale === code ? (locales[0]?.code || '') : current.defaultLocale });
    if (activeLocale === code) setActiveLocale(locales[0]?.code || '');
  };

  const setFieldTranslation = (componentId, patch) => {
    const localeMap = current.translations?.[activeLocale] || {};
    set({
      translations: {
        ...current.translations,
        [activeLocale]: { ...localeMap, [componentId]: { ...localeMap[componentId], ...patch } },
      },
    });
  };

  const getFieldTranslation = (componentId) => current.translations?.[activeLocale]?.[componentId] || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-[560px] bg-white rounded-t-lg sm:rounded-lg shadow-xl p-4 sm:p-6 max-h-[100dvh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5] shrink-0">
          <p className="text-[16px] font-bold text-[#2E2E2E]">Languages</p>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
        </div>

        <div className="mb-4 shrink-0">
          <label className={labelClass}>Add a Language</label>
          <div className="flex flex-wrap gap-1.5">
            {COMMON_LOCALES.filter((l) => !current.locales?.some((cl) => cl.code === l.code)).map((l) => (
              <button
                key={l.code}
                onClick={() => addLocale(l.code)}
                className="flex items-center gap-1 px-2 py-1 text-[11px] font-bold text-[#0176D3] border border-[#D8DDE6] rounded-[4px] hover:border-[#0176D3] whitespace-nowrap"
              >
                <Plus size={11} /> {l.label}
              </button>
            ))}
          </div>
        </div>

        {!current.locales?.length ? (
          <p className="text-[11px] text-[#706E6B]">Add a language above to start translating this form's field labels.</p>
        ) : (
          <>
            <div className="flex items-center gap-1.5 mb-4 shrink-0 flex-wrap">
              {current.locales.map((l) => (
                <div key={l.code} className="flex items-center">
                  <button
                    onClick={() => setActiveLocale(l.code)}
                    className={`px-3 py-1.5 text-[12px] font-bold rounded-l-[4px] border  whitespace-nowrap ${activeLocale === l.code ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white text-[#181818] border-[#D8DDE6]'}`}
                  >
                    {l.label}{current.defaultLocale === l.code ? ' (default)' : ''}
                  </button>
                  <button
                    onClick={() => removeLocale(l.code)}
                    className={`px-1.5 py-1.5 rounded-r-[4px] border border-l-0  whitespace-nowrap ${activeLocale === l.code ? 'bg-[#0176D3] border-[#0176D3]' : 'bg-white border-[#D8DDE6]'} text-[#706E6B] hover:text-[#EA001E]`}
                    title="Remove language"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
            </div>

            {activeLocale && (
              <div className="overflow-y-auto space-y-4 pr-1">
                {pages.map((page) => (
                  <div key={page.id} className="space-y-3">
                    {page.title && (
                      <div>
                        <label className={labelClass}>Page Title (original: "{page.title}")</label>
                        <input
                          className={inputClass}
                          value={current.translations?.[activeLocale]?.[`page:${page.id}`]?.title || ''}
                          onChange={(e) => setFieldTranslation(`page:${page.id}`, { title: e.target.value })}
                          placeholder={page.title}
                        />
                      </div>
                    )}
                    {(page.components || []).map((c) => {
                      const t = getFieldTranslation(c.id);
                      return (
                        <div key={c.id} className="border border-[#F0F1F3] rounded-[4px] p-3 space-y-2">
                          <p className="text-[11px] font-bold text-[#706E6B]">Original: "{c.label || c.text || c.id}"</p>
                          <input
                            className={inputClass}
                            value={t.label || ''}
                            onChange={(e) => setFieldTranslation(c.id, { label: e.target.value })}
                            placeholder={c.label || 'Label'}
                          />
                          {PLACEHOLDER_TYPES.has(c.type) && (
                            <input
                              className={inputClass}
                              value={t.placeholder || ''}
                              onChange={(e) => setFieldTranslation(c.id, { placeholder: e.target.value })}
                              placeholder={c.placeholder || 'Placeholder'}
                            />
                          )}
                          {c.type === 'checkbox' && (
                            <input
                              className={inputClass}
                              value={t.checkboxLabel || ''}
                              onChange={(e) => setFieldTranslation(c.id, { checkboxLabel: e.target.value })}
                              placeholder={c.checkboxLabel || 'Checkbox label'}
                            />
                          )}
                          {OPTION_TYPES.has(c.type) && (c.options || []).map((opt, i) => (
                            <input
                              key={opt.value}
                              className={inputClass}
                              value={t.options?.[i] || ''}
                              onChange={(e) => {
                                const options = [...(t.options || [])];
                                options[i] = e.target.value;
                                setFieldTranslation(c.id, { options });
                              }}
                              placeholder={`Option: ${opt.label}`}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
